import {
    type NodeCfg,
    type NodeSocketKindMap,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { RequiredSockets, UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';

export const goToTabUiNodeSpec: UiNodeSpec<'goToTab'> = {
    nodeCategory: 'tab',
    kindLabel: 'Activate Specific Tab',
    controls: getNodeCfg => {
        const ret: ReturnType<UiNodeSpec<'goToTab'>['controls']> = {
            tabGroupDetails: {
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.tabGroupDetails,
                cfg: {
                    useSync: true,
                    kind: 'selectTabGroup',
                    getValue: n => n.kind === 'goToTab' ? n.controlValues.tabGroupDetails.id : null,
                    onSelect: tabGroupId => {
                        const entityData = accessor.entityAsType('appletDesignVersion');
                        if (!entityData) {
                            throw new Error('No applet design version found in store');
                        }

                        let tabsFromTabGroup: Latest.Screen.Designer.TabInstanceCell[] | null = null;
                        for (const screen of entityData.config.screens) {
                            for (const untypedAspectRatio in screen.gridLayoutAspectRatios) {
                                const typedAspectRatio = untypedAspectRatio as Latest.Screen.Designer.GridLayoutAspectRatio;
                                const screenGridCells = screen.gridLayoutAspectRatios[typedAspectRatio].linkedGridCells;
                                if (screenGridCells.find((gc): gc is Latest.Screen.Designer.TabGroupCell => gc.id === tabGroupId && gc.type === 'tabGroup')) {
                                    tabsFromTabGroup = screenGridCells.filter((gc): gc is Latest.Screen.Designer.TabInstanceCell => gc.type === 'tabInstance' && gc.parentGridId === tabGroupId);
                                    break;
                                }
                            }
                            if (tabsFromTabGroup) {
                                break;
                            }
                        }

                        if (!tabsFromTabGroup) {
                            throw new Error(`Tab group with ID ${tabGroupId} has no tabs.`);
                        }

                        const nodeCfg = getNodeCfg();
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    tabGroupDetails: {
                                        id: tabGroupId,
                                        tabs: tabsFromTabGroup.map(t => {
                                            return {
                                                tabIndex: t.idx,
                                            };
                                        }),
                                    },
                                },
                            },
                        });
                    },
                },
            },
        };
        return ret;
    },
    cfg: () => {
        const goToTabNodeCfg: NodeCfg<'goToTab'> = {
            id: getUID(),
            kind: 'goToTab',
            controlValues: {
                tabGroupDetails: {
                    id: '',
                    tabs: [],
                },
            },
        };
        return goToTabNodeCfg;
    },
    inputs: nodeCfg => {
        const inputs: ReturnType<UiNodeSpec<'goToTab'>['inputs']> = {};
        for (const tabIdx of nodeCfg.controlValues.tabGroupDetails.tabs) {
            inputs[`${tabIdx.tabIndex}`] = { socket: 'event', label: `Tab ${tabIdx.tabIndex + 1}` };
        }
        return inputs;
    },
    outputs: () => {
        const ret: ReturnType<UiNodeSpec<'goToTab'>['outputs']> = {
            next: { socket: 'event', label: 'Next' },
            activeTabIndex: { socket: 'number', label: 'Active Tab Index' },
        };
        return ret;
    },
    requiredSockets: (node, availableInputSockets) => {
        const ret: ReturnType<RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>> = {
            required: [],
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: availableInputSockets,
                },
            ],
        };
        return ret;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.tabGroupDetails.id) {
            errors.tabGroupId = 'Tab group is required';
        }
        const entityData = accessor.entityAsType('appletDesignVersion');
        if (!entityData) {
            throw new Error('No applet design version found in store');
        }

        let tabsFromTabGroup: Latest.Screen.Designer.TabInstanceCell[] | null = null;
        for (const screen of entityData.config.screens) {
            for (const untypedAspectRatio in screen.gridLayoutAspectRatios) {
                const typedAspectRatio = untypedAspectRatio as Latest.Screen.Designer.GridLayoutAspectRatio;
                const screenGridCells = screen.gridLayoutAspectRatios[typedAspectRatio].linkedGridCells;
                if (screenGridCells.find((gc): gc is Latest.Screen.Designer.TabGroupCell => gc.id === node.controlValues.tabGroupDetails.id && gc.type === 'tabGroup')) {
                    tabsFromTabGroup = screenGridCells.filter((gc): gc is Latest.Screen.Designer.TabInstanceCell => gc.type === 'tabInstance' && gc.parentGridId === node.controlValues.tabGroupDetails.id);
                    break;
                }
            }
            if (tabsFromTabGroup) {
                break;
            }
        }

        if (!tabsFromTabGroup) {
            errors.tabs = 'No tabs found for the selected tab group';
        } else if (tabsFromTabGroup.length !== node.controlValues.tabGroupDetails.tabs.length) {
            errors.tabs = 'Tab selection does not match the number of tabs in the group';
        }

        if (!node.controlValues.tabGroupDetails.tabs.length) {
            errors.tabs = 'At least one tab is required';
        }
        return Object.keys(errors).length === 0 ? true : errors;
    },
};
